import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

type DraftKey = `user:${number}-game:${number}:team:${number}` | `user:${number}-game:${number}:player:${number}`;

interface BaseDraft {
	userId: number;
	gameId: number;
	reviewText: string | null;
	attachedEventIds: number[] | null;
	contentId: string | null;
	generatedWithAI: boolean;
}

interface TeamDraft extends BaseDraft {
	kind: "team";
	teamId: number;
}

interface PlayerDraft extends BaseDraft {
	kind: "player";
	playerId: number;
	assignedBadgeId: number | null;
}

type Draft = TeamDraft | PlayerDraft;

type TeamDraftIdentity = Pick<TeamDraft, "userId" | "kind" | "gameId" | "teamId">;
type PlayerDraftIdentity = Pick<PlayerDraft, "userId" | "kind" | "gameId" | "playerId">;
type DraftIdentity = TeamDraftIdentity | PlayerDraftIdentity;

type DraftUpsert =
	| (TeamDraftIdentity & Partial<Omit<TeamDraft, keyof TeamDraftIdentity>>)
	| (PlayerDraftIdentity & Partial<Omit<PlayerDraft, keyof PlayerDraftIdentity>>);

interface ToggleState {
	teamKey: DraftKey | null;
	playerKey: DraftKey | null;
}

interface CoachReviewsStoreState {
	drafts: Partial<Record<DraftKey, Draft>>;
	toggleState: ToggleState;
	isLoading: boolean;
	accessToken: string | null;
}

interface CoachReviewsStoreActions {
	upsertDraft: (draft: DraftUpsert) => void;
	clearDraft: (draftKey: DraftKey) => void;
	toggleOpen: (identity: DraftIdentity) => void;
	setIsLoading: (isLoading: boolean) => void;
	setAccessToken: (accessToken: string | null) => void;
}

type CoachReviewsStore = CoachReviewsStoreState & CoachReviewsStoreActions;

export const getDraftKey = (identity: DraftIdentity): DraftKey => {
	return identity.kind === "team"
		? `user:${identity.userId}-game:${identity.gameId}:team:${identity.teamId}`
		: `user:${identity.userId}-game:${identity.gameId}:player:${identity.playerId}`;
};

const createEmptyDraft = (identity: DraftIdentity): Draft => {
	const base: BaseDraft = {
		userId: identity.userId,
		gameId: identity.gameId,
		reviewText: null,
		attachedEventIds: null,
		contentId: null,
		generatedWithAI: false,
	};

	return identity.kind === "team"
		? { ...base, kind: "team", teamId: identity.teamId }
		: { ...base, kind: "player", playerId: identity.playerId, assignedBadgeId: null };
};

export const useCoachReviewsStore = create<CoachReviewsStore>()(
	persist(
		(set) => ({
			drafts: {},
			toggleState: {
				teamKey: null,
				playerKey: null,
			},
			isLoading: false,
			accessToken: null,
			setIsLoading: (isLoading: boolean) => set({ isLoading }),
			setAccessToken: (accessToken: string | null) => set({ accessToken }),
			upsertDraft: (draft) => {
				const draftKey = getDraftKey(draft);
				set((state) => {
					const existingDraft = state.drafts[draftKey] ?? createEmptyDraft(draft);

					return {
						drafts: {
							...state.drafts,
							[draftKey]: {
								...existingDraft,
								...draft,
							},
						},
					};
				});
			},
			clearDraft: (draftKey) => {
				set((state) => {
					const { [draftKey]: _removed, ...rest } = state.drafts;
					return { drafts: rest };
				});
			},
			toggleOpen: (identity) => {
				const draftKey = getDraftKey(identity);
				set((state) => {
					const isTeam = identity.kind === "team";
					return {
						toggleState: {
							teamKey: isTeam ? (state.toggleState.teamKey === draftKey ? null : draftKey) : state.toggleState.teamKey,
							playerKey: !isTeam ? (state.toggleState.playerKey === draftKey ? null : draftKey) : state.toggleState.playerKey,
						},
					};
				});
			},
		}),
		{
			name: "coach-reviews-store",
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({ drafts: state.drafts }),
		}
	)
);
